import React from 'react';
import { withRouter, Switch, Route } from 'react-router-dom';
import { language } from '../../services/LanguageService';
import { tracking } from '../../services/TrackingService';
import { getPathPrefix, getAppIdFromPath, getEnvironmentVar } from '../../helpers/helpers';
import { AsperaHeader } from '../AsperaHeader';
import { getRecursiveRoutes } from '../../helpers/components';
import { AsperaLeftNav } from '../AsperaLeftNav';
import { AsperaRouteMonitorRender } from './AsperaRouteMonitorRender';
import { AsperaErrorState } from '../AsperaErrorState';
import { ReplaySubject } from 'rxjs';
import { AsperaDesktopLeftNav } from '../AsperaDesktopLeftNav';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { AsperaSessionMonitor } from '../AsperaSessionMonitor';
import { InlineNotification, Link } from '../Carbon';
/**
 * React Component
 *
 * The AsperaRouteMonitor provides a listener for route changes and blocks the app as appropriate
 * Supports auto blocking for no app access and geofencing requirements
 *
 * @example
 * ```javascript
 * <AsperaRouteMonitor availableApps={availableApps} geofencingSettings={mySettings}>
 *   <h1>Inner child that should show normally</h1>
 * </AsperaRouteMonitor>
 * ```
 */
export const AsperaRouteMonitor = withRouter(class AsperaRouteMonitor extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            leftNavExpanded: false,
            desktopLeftNavExpanded: false,
        };
        this.subManager = new SubManager();
        /** Indicate if check for geofencing has happened to avoid multiple, if undefined the check has not happened */
        this.disabledByGeo = undefined;
        /** Exempt base paths */
        // eslint-disable-next-line
        this.exemptBasePaths = ['help', 'settings', 'public'];
        /** React Router DOM Items to Render */
        this.routesToRender = [];
        /** Desktop replay subject for left nav status */
        this.desktopLeftNavSubject = new ReplaySubject(1);
        this.setLeftNav = (expanded) => {
            this.setState({ leftNavExpanded: expanded });
        };
    }
    get defaultNotFoundProps() {
        const { history } = this.props;
        return {
            type: '404',
            title: language.get('errors.pageNotFound'),
            body: language.get('errors.pageNotFoundDescription'),
            links: [
                {
                    name: language.get('navItems.home'),
                    onClick: () => {
                        history.push('/');
                    },
                },
            ],
        };
    }
    /** Error props for no access to certain app */
    getNoAccessToApp() {
        const { currentApp, noAccessToAppError } = this.props;
        if (typeof noAccessToAppError === 'function') {
            return noAccessToAppError(currentApp.displayName);
        }
        return {
            title: language.get('errors.noAccessToApp', [currentApp.displayName]),
            body: language.get('errors.noAccessToAppDescription'),
        };
    }
    /** Error props for no access to any application */
    get noAccessToSystem() {
        const { noAccessToSystemError } = this.props;
        if (noAccessToSystemError) {
            return noAccessToSystemError;
        }
        return {
            title: language.get('errors.noAccessToSystem'),
            body: language.get('errors.noAccessToSystemDescription'),
        };
    }
    /** Error props for no access due to geofencing */
    get noAccessGeo() {
        const { noAccessToGeoError } = this.props;
        if (noAccessToGeoError) {
            return noAccessToGeoError;
        }
        return {
            title: language.get('errors.noAccessToGeo'),
            body: language.get('errors.noAccessToGeoDescription'),
        };
    }
    get desktopMode() {
        return !!getEnvironmentVar('desktop_mode');
    }
    onRouteChanged() {
        const { location, newAppListener, currentApp, onRouteChange } = this.props;
        const appId = getAppIdFromPath(location.pathname);
        if (currentApp.id !== appId) {
            newAppListener(appId);
        }
        if (onRouteChange && typeof onRouteChange === 'function') {
            onRouteChange(location.pathname);
        }
        tracking.triggerNpsChange();
        this.setState({});
    }
    isGeoBlocked() {
        //TODO: use HERE and setup geofencing
        if (this.disabledByGeo === undefined) {
            this.disabledByGeo = false;
        }
        return this.disabledByGeo;
    }
    isExemptRoute() {
        const { location } = this.props;
        const basePath = location.pathname.split('/')[1];
        return !!(basePath && this.exemptBasePaths.indexOf(basePath.toLowerCase()) > -1);
    }
    isAnyAppAvailable() {
        const { availableApps, disableAppBlocking } = this.props;
        if (window.walkmeData) {
            availableApps.forEach(item => {
                // For some reason without nested IF the switch doesn't think its safe
                if (window.walkmeData) {
                    switch (item.id) {
                        case 'activity':
                            window.walkmeData.hasAppActivity = true;
                            break;
                        case 'admin':
                            window.walkmeData.hasAppAdmin = true;
                            break;
                        case 'automation':
                            window.walkmeData.hasAppAutomation = true;
                            break;
                        case 'files':
                            window.walkmeData.hasAppFiles = true;
                            break;
                        case 'packages':
                            window.walkmeData.hasAppPackages = true;
                            break;
                        case 'faspio':
                            window.walkmeData.hasAppFaspio = true;
                            break;
                    }
                }
            });
        }
        if (this.isExemptRoute() || disableAppBlocking) {
            return true;
        }
        return !!availableApps.length;
    }
    isCurrentAppAvailable() {
        const { currentApp, getAvailableAppById, disableAppBlocking } = this.props;
        if (this.isExemptRoute() || disableAppBlocking) {
            return true;
        }
        return !!getAvailableAppById(currentApp.id);
    }
    isAppRoute() {
        const { location, allRoutes } = this.props;
        if (location.pathname === '/') {
            return false;
        }
        return allRoutes.filter(route => !route.hidden).map(route => route.path.replace('/', '')).indexOf(getPathPrefix(location.pathname)) > -1;
    }
    get headerSharedProps() {
        const { helpOptions, currentApp, productName, currentUser, availableApps, settingsOnClick, preLogoutActions, hideAppSwitcher, onLoginClick, searchData, brandingOptions, customMenuItem, leftNavProps, disableTransferMonitor, customUserIcon, changeDefaultApp, customHelpIcon, transferMonitorExtraOptions, notificationSystemControls } = this.props;
        return {
            preLogoutActions: preLogoutActions,
            productName: productName,
            currentApp: currentApp,
            currentUser: currentUser,
            appOptions: availableApps,
            helpOptions: helpOptions,
            settingsOnClick: settingsOnClick,
            hideAppSwitcher: hideAppSwitcher,
            onLoginClick: onLoginClick,
            searchData: searchData,
            brandingOptions: brandingOptions,
            customMenuItem: customMenuItem,
            leftNavProps: leftNavProps,
            disableTransferMonitor: disableTransferMonitor,
            customUserIcon: customUserIcon,
            customHelpIcon: customHelpIcon,
            changeDefaultApp: changeDefaultApp,
            transferMonitorExtraOptions: transferMonitorExtraOptions,
            notificationSystemControls: notificationSystemControls,
        };
    }
    get notificationPageAlert() {
        const { notificationSystemControls } = this.props;
        if (notificationSystemControls && notificationSystemControls.bannerNotification) {
            return React.createElement(InlineNotification, { onClose: notificationSystemControls.bannerNotification.onClose, className: "banner-notification", kind: notificationSystemControls.bannerNotification.kind, lowContrast: true, title: notificationSystemControls.bannerNotification.text, subtitle: notificationSystemControls.bannerNotification.action ? React.createElement(Link, { onClick: notificationSystemControls.bannerNotification.action.onClick }, notificationSystemControls.bannerNotification.action.text) : undefined });
        }
        return null;
    }
    viewWithTopBar(viewContent) {
        const { hideHeader } = this.props;
        return (React.createElement("div", null,
            !hideHeader && React.createElement(AsperaHeader, Object.assign({}, this.headerSharedProps)),
            viewContent));
    }
    getStandardView() {
        const { hideLeftNav, leftNavProps, notFoundProps, className, leftNavAdvertisement, hideHeader, productName, preLogoutActions } = this.props;
        const { leftNavExpanded, desktopLeftNavExpanded } = this.state;
        return (React.createElement(React.Fragment, null,
            !hideHeader && React.createElement(AsperaHeader, Object.assign({ leftNavExpanded: leftNavExpanded, setLeftNav: this.setLeftNav }, this.headerSharedProps)),
            !!leftNavProps && (this.desktopMode ? React.createElement(AsperaDesktopLeftNav, Object.assign({}, leftNavProps, { productName: productName, desktopLeftNavSubject: this.desktopLeftNavSubject, headerIsHidden: hideHeader })) : React.createElement(AsperaLeftNav, Object.assign({}, leftNavProps, { headerIsHidden: hideHeader, hideLeftNav: hideLeftNav, expanded: leftNavExpanded, setLeftNav: this.setLeftNav, leftNavAdvertisement: leftNavAdvertisement }))),
            React.createElement("div", { className: `app-wrapper aspera-route-monitor ${this.desktopMode ? 'aspera-desktop-wrapper' : ''} ${hideLeftNav ? '' : 'has-left-nav'} ${className ? className : ''} ${hideHeader ? 'hide-header' : ''} ${desktopLeftNavExpanded ? 'desktop-left-nav-expanded' : ''}` },
                this.notificationPageAlert,
                React.createElement(AsperaSessionMonitor, { preLogoutActions: preLogoutActions }),
                React.createElement(Switch, null,
                    this.routesToRender.map(route => route),
                    React.createElement(Route, { path: "*" },
                        React.createElement("div", { className: "cds--grid error-wrapping" },
                            React.createElement(AsperaErrorState, Object.assign({}, (notFoundProps || this.defaultNotFoundProps)))))))));
    }
    getErrorView(props) {
        const { className, hideHeader } = this.props;
        const { desktopLeftNavExpanded } = this.state;
        return React.createElement("div", { className: `app-wrapper aspera-route-monitor full-page-error ${className ? className : ''} ${hideHeader ? 'hide-header' : ''} ${desktopLeftNavExpanded ? 'desktop-left-nav-expanded' : ''}` },
            React.createElement(AsperaErrorState, Object.assign({}, props)));
    }
    getView() {
        if (this.isGeoBlocked()) {
            return this.getErrorView(this.noAccessGeo);
        }
        else if (!this.isAnyAppAvailable()) {
            return this.viewWithTopBar(this.getErrorView(this.noAccessToSystem));
        }
        else if (!this.isAppRoute()) {
            return this.getStandardView();
        }
        else if (!this.isCurrentAppAvailable()) {
            return this.viewWithTopBar(this.getErrorView(this.getNoAccessToApp()));
        }
        else {
            return this.getStandardView();
        }
    }
    componentDidUpdate(previousProps) {
        const { location, hideLeftNav } = this.props;
        if (location !== previousProps.location || hideLeftNav !== previousProps.hideLeftNav) {
            this.onRouteChanged();
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    componentDidMount() {
        const { allRoutes } = this.props;
        this.onRouteChanged();
        this.routesToRender = getRecursiveRoutes(allRoutes);
        this.subManager.setSubscription(this.desktopLeftNavSubject.subscribe({
            next: expanded => {
                this.setState({ desktopLeftNavExpanded: expanded });
            },
            error: error => {
                new AsperaError('AsperaRouteMonitor: unable to watch for desktop left nav changes', error);
            },
        }));
    }
    render() {
        return (React.createElement(React.Fragment, null,
            this.getView(),
            React.createElement(AsperaRouteMonitorRender, null)));
    }
});
